import React from 'react';
import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  formatDateTime, formatMW, useContainerWidth, EmptyState,
  CHART_MARGINS, defaultLegendProps, tickLabelStyle, rotatedTickLabelStyle,
} from './shared';

/**
 * Asset Utilization Chart
 *
 * Compares available capacity vs actual dispatch (MW) over time.
 * Shows how much portfolio capacity sits idle — low dispatch-to-availability
 * ratio signals uncompetitive bidding or market oversupply.
 */
const UtilizationChart = ({ data, assets, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return <EmptyState message="No utilization data available" />;

  // Aggregate across assets per bucket for portfolio-level view
  const buckets = {};
  data.forEach(({ date_time, avg_availability, avg_dispatched }) => {
    const key = String(date_time);
    if (!buckets[key]) buckets[key] = { availability: 0, dispatched: 0 };
    buckets[key].availability += avg_availability;
    buckets[key].dispatched += avg_dispatched;
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const series = [
    {
      type: 'bar',
      label: 'Available (MW)',
      data: sortedBuckets.map((b) => buckets[b].availability),
      color: '#e5e7eb',
    },
    {
      type: 'bar',
      label: 'Dispatched (MW)',
      data: sortedBuckets.map((b) => buckets[b].dispatched),
      color: '#D98A2B',
    },
  ];

  return (
    <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <BarChart
          width={width}
          height={420}
          series={series}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatMW, tickLabelStyle }]}
          margin={CHART_MARGINS}
          slotProps={{ legend: { ...defaultLegendProps } }}
        />
      )}
    </Box>
  );
};

export default UtilizationChart;
